<script setup lang="ts">
import ButtonComponent from "@/builder/components/button/ButtonComponent.vue";
import { ActionTrigger, ButtonActionType } from '@/builder/components/button/ButtonInterface';
import { Frequency, PromptDecision } from "@/builder/enums";
import { IRecurringAskPromptComponent, RecurringAskOption } from '@/builder/interfaces';
import { useRenderStore } from '@/builder/stores/renderStore';
import { frequencyDisplayName } from '@/enums';
import { amountToNumber } from "@/utilities/NumberUtilities";
import { formatCurrency } from '@/utilities/StringUtilities';
import { PropType, computed, onBeforeMount, ref } from 'vue';

const props = defineProps({
    component: {
        type: Object as PropType<IRecurringAskPromptComponent>,
        required: true
    }
});

const renderStore = useRenderStore();

//refs
const asks = ref<RecurringAskOption[]>(props.component.customData.asks);
const optOutButton = ref(props.component.customData.optOutButton);
const backgroundColor = ref(renderStore.builderMode ? 'rd-bg-neutral-9' : 'rd-bg-white');

//computed
const askOptions = computed(() => {
    //return only unique options based on text
    return asks.value.filter((ask, index, self) =>
        index === self.findIndex((t) => (
            t.button.customData.text === ask.button.customData.text
        ))
    );
});

//methods
function getAskButtonText(ask: RecurringAskOption) {
    return `Donate $${askAmountCurrency(ask.multiplier)}/${askFrequencyDisplay(ask.frequency)}`;
}

function askAmountCurrency(multiplier: number) {
    return formatCurrency(askAmount(multiplier).toString());
}

function askFrequencyDisplay(frequency: Frequency) {
    return frequencyDisplayName(frequency).toLocaleLowerCase();
}

function askAmount(multiplier: number) {
    const askAmount = amountToNumber(renderStore.amount) * multiplier;

    if (askAmount < 5) {
        return 5; // giving has a minimum of $5
    }
    else {
        return askAmount;
    }
}

function acceptAsk(ask: RecurringAskOption) {
    if (renderStore.gift.amount) {
        const giftAmount = askAmount(ask.multiplier);
        renderStore.setPromptAmount(giftAmount, ask.multiplier);
        renderStore.setFrequency(ask.frequency);
    }

    renderStore.resolvePrompt(props.component.id, PromptDecision.Accept);
}

function declineAsk() {
    renderStore.resolvePrompt(props.component.id, PromptDecision.Decline);
}

function setOptOutButton() {
    if (!optOutButton.value) return;

    optOutButton.value.customData.action = {
        type: ButtonActionType.Custom,
        trigger: ActionTrigger.Click,
        values: declineAsk,
    }
}

function setAskButtons() {
    if (!asks.value) return;
    if (asks.value.length === 0) return;

    asks.value.forEach((ask: RecurringAskOption) => {
        ask.button.customData.text = getAskButtonText(ask);
        ask.button.customData.action = {
            type: ButtonActionType.Custom,
            trigger: ActionTrigger.Click,
            values: () => acceptAsk(ask),
        }
    });
}

//life cycle
onBeforeMount(() => {
    setOptOutButton();
    setAskButtons();
});

</script>

<template>
    <section class="rd-flex rd-flex-col rd-gap-y-2.5 rd-outline-none recurring-ask-container"
        :class="[backgroundColor, renderStore.builderMode ? 'rd-mt-6 rd-p-5' : 'rd-mt-12']"
        aria-label="Recurring Gift Ask Buttons" tabindex="0">

        <template v-for="ask in askOptions" :key="ask.button.id">
            <ButtonComponent :component="ask.button" :class="{ 'rd-pointer-events-none': renderStore.builderMode }" />
        </template>

        <ButtonComponent :component="component.customData.optOutButton"
            :class="{ 'rd-border rd-border-neutral-6 rd-pointer-events-none': renderStore.builderMode }" />
    </section>
</template>
